import template from './json-mapper-editor.html';
import JsonMapDefinitionModel from '../../lib/models/json_mapper/JsonMapDefinitionModel.js';
import JsonMapFieldModel from '../../lib/models/json_mapper/JsonMapFieldModel.js';

/**
 * JSON Mapper Editor component.
 * Provides inline viewing, editing, and creation of JSON map definitions and their fields.
 * @kind component
 * @class JsonMapperEditor
 *
 * @param {observable} map_definition_id - Two-way bound observable for the selected map definition ID
 * @param {observable} [namespace_hint] - Optional namespace to pre-fill when creating new definitions
 */
class JsonMapperEditorVM
{
	constructor(params)
	{
		this.params = params;
		this.map_definition_id = params.map_definition_id;
		this.namespace_hint = ko_helper.safe_observable(params.namespace_hint, '');

		this.mapDefinitions = ko.observableArray([]);
		this.selectedDefinition = ko.observable(null);
		this.mapFields = ko.observableArray([]);
		this.transformTypes = ko.observableArray([]);

		this.showCreateForm = ko.observable(false);
		this.showDefinitionDetails = ko.observable(false);
		this.showTestPanel = ko.observable(false);

		this.editingFieldId = ko.observable(null);

		// New definition form
		this.newDef = {
			namespace: ko.observable(''),
			name: ko.observable(''),
			description: ko.observable(''),
			base_object: ko.observable('{}')
		};

		// Inline edit field form
		this.editField = {
			map_field_id: ko.observable(null),
			map_definition_id: ko.observable(null),
			field_name: ko.observable(''),
			idx: ko.observable(0),
			source_selector: ko.observable(''),
			target_path_text: ko.observable(''),
			transform_type: ko.observable(''),
			transform_options: ko.observable('')
		};

		// Test panel
		this.testSource = ko.observable('{}');
		this.testTemplate = ko.observable('');
		this.testResult = ko.observable('');
		this.testError = ko.observable('');

		this.map_definition_id.subscribe((id) => {
			if (id)
				this.loadDefinitionById(id);
			else
				this.clearSelection();
		});

		this.formattedBaseObject = ko.computed(() => {
			const def = this.selectedDefinition();
			if (!def)
				return '';
			try
			{
				const parsed = JSON.parse(def.base_object());
				return JSON.stringify(parsed, null, '\t');
			}
			catch (e)
			{
				return def.base_object();
			}
		});

		this.init();
	}

	async init()
	{
		await Promise.all([
			this.loadDefinitions(),
			this.loadTransformTypes()
		]);

		const currentId = this.map_definition_id();
		if (currentId)
			this.loadDefinitionById(currentId);
	}

	async loadDefinitions()
	{
		const result = await Grape.tables.select({
			schema: 'json_mapper',
			table: 'map_definition'
		});
		this.mapDefinitions(result.records || []);
	}

	async loadTransformTypes()
	{
		const result = await Grape.tables.select({
			schema: 'plugins',
			table: 'v_plugin_consumer',
			filter: [{ field: 'plugin_hook', operator: '=', value: 'json_mapper.transform_value' }]
		});
		this.transformTypes(result.records || []);
	}

	async loadDefinitionById(id)
	{
		const defs = this.mapDefinitions();
		const found = defs.find(d => d.map_definition_id == id);
		if (found)
		{
			this.selectedDefinition(new JsonMapDefinitionModel(found));
			await this.loadFields(id);
		}
	}

	async loadFields(mapDefinitionId)
	{
		const result = await Grape.fetches.getJSON('/api/record', {
			schema: 'json_mapper',
			table: 'v_json_map_fields',
			filter: [{ field: 'map_definition_id', operator: '=', value: mapDefinitionId }],
			sortfield: 'idx',
			sortorder: 'ASC'
		});
		this.mapFields(result.records || []);
	}

	clearSelection()
	{
		this.selectedDefinition(null);
		this.mapFields([]);
		this.editingFieldId(null);
	}

	onSelectDefinition(def)
	{
		this.map_definition_id(def.map_definition_id);
		this.showCreateForm(false);
	}

	btnToggleCreateForm()
	{
		const show = !this.showCreateForm();
		this.showCreateForm(show);
		if (show)
		{
			this.newDef.namespace(this.namespace_hint() || '');
			this.newDef.name('');
			this.newDef.description('');
			this.newDef.base_object('{}');
		}
	}

	async btnSaveNewDefinition()
	{
		try
		{
			const ns = this.newDef.namespace().trim();
			const name = this.newDef.name().trim();
			if (!ns || !name)
				throw new Error('Namespace and Name are required');

			let base_object;
			try
			{
				base_object = JSON.parse(this.newDef.base_object());
			}
			catch (e)
			{
				throw new Error('Base Object must be valid JSON');
			}

			const result = await Grape.fetches.postJSON('/api/json-map/definition', {
				namespace: ns,
				name: name,
				description: this.newDef.description(),
				base_object: base_object
			});

			if (result.status !== 'OK')
				throw new Error(result.message || 'Failed to create map definition');

			Grape.alerts.alert({ type: 'success', title: 'Created', message: 'Map definition created' });
			this.showCreateForm(false);
			await this.loadDefinitions();

			if (result.map_definition_id)
				this.map_definition_id(result.map_definition_id);
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
		}
	}

	btnToggleDetails()
	{
		this.showDefinitionDetails(!this.showDefinitionDetails());
	}

	async btnSaveDefinition()
	{
		const def = this.selectedDefinition();
		if (!def)
			return;

		try
		{
			const result = await def.save();
			if (result.status !== 'OK')
				throw new Error(result.message || 'Failed to save');

			Grape.alerts.alert({ type: 'success', title: 'Saved', message: 'Map definition updated' });
			await this.loadDefinitions();
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
		}
	}

	btnClearSelection()
	{
		this.map_definition_id(null);
	}

	// --- Field operations ---

	btnAddField()
	{
		const def = this.selectedDefinition();
		if (!def)
			return;

		const fields = this.mapFields();
		const maxIdx = fields.reduce((m, f) => Math.max(m, f.idx || 0), 0);

		this.editField.map_field_id(null);
		this.editField.map_definition_id(def.map_definition_id());
		this.editField.field_name('');
		this.editField.idx(maxIdx + 1);
		this.editField.source_selector('');
		this.editField.target_path_text('');
		this.editField.transform_type('');
		this.editField.transform_options('');
		this.editingFieldId('__new__');
	}

	btnEditField(field)
	{
		this.editField.map_field_id(field.map_field_id);
		this.editField.map_definition_id(field.map_definition_id);
		this.editField.field_name(field.field_name || '');
		this.editField.idx(field.idx != null ? field.idx : 0);
		this.editField.source_selector(field.source_selector || '');
		const tp = Array.isArray(field.target_path) ? field.target_path.join('.') : (field.target_path || '');
		this.editField.target_path_text(tp);
		this.editField.transform_type(field.transform_type || '');
		this.editField.transform_options(field.transform_options ? JSON.stringify(field.transform_options) : '');
		this.editingFieldId(field.map_field_id);
	}

	btnCancelEditField()
	{
		this.editingFieldId(null);
	}

	async btnSaveField()
	{
		try
		{
			const fieldName = this.editField.field_name().trim();
			if (!fieldName)
				throw new Error('Field name is required');

			const targetText = this.editField.target_path_text().trim();
			const targetPath = targetText ? targetText.split('.') : [];

			let transformOptions = null;
			const toText = (this.editField.transform_options() || '').trim();
			if (toText)
			{
				try { transformOptions = JSON.parse(toText); }
				catch (e) { throw new Error('Transform Options must be valid JSON'); }
			}

			const payload = {
				map_definition_id: this.editField.map_definition_id(),
				field_name: fieldName,
				idx: this.editField.idx(),
				source_selector: this.editField.source_selector(),
				target_path: targetPath,
				transform_type: this.editField.transform_type() || null,
				transform_options: transformOptions
			};

			if (this.editField.map_field_id())
				payload.map_field_id = this.editField.map_field_id();

			const result = await Grape.fetches.postJSON('/api/json-map/field', payload);
			if (result.status !== 'OK')
				throw new Error(result.message || 'Failed to save field');

			this.editingFieldId(null);
			await this.loadFields(this.editField.map_definition_id());
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
		}
	}

	async btnDeleteField(field)
	{
		const confirmed = await Grape.alerts.confirm({
			type: 'warning',
			title: 'Delete Field',
			message: `Delete field "${field.field_name}"?`
		});
		if (!confirmed)
			return;

		try
		{
			const result = await Grape.fetches.deleteJSON('/api/json-map/field', {
				map_field_id: field.map_field_id
			});

			if (result.status !== 'OK')
				throw new Error(result.message || 'Failed to delete');

			await this.loadFields(field.map_definition_id);
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
		}
	}

	async btnMoveFieldUp(field)
	{
		const fields = this.mapFields();
		const currentIdx = fields.findIndex(f => f.map_field_id === field.map_field_id);
		if (currentIdx <= 0)
			return;

		const prev = fields[currentIdx - 1];
		await this.swapFieldIdx(field, prev);
	}

	async btnMoveFieldDown(field)
	{
		const fields = this.mapFields();
		const currentIdx = fields.findIndex(f => f.map_field_id === field.map_field_id);
		if (currentIdx < 0 || currentIdx >= fields.length - 1)
			return;

		const next = fields[currentIdx + 1];
		await this.swapFieldIdx(field, next);
	}

	async swapFieldIdx(fieldA, fieldB)
	{
		try
		{
			const idxA = fieldA.idx;
			const idxB = fieldB.idx;

			await Grape.fetches.postJSON('/api/json-map/field', {
				map_field_id: fieldA.map_field_id,
				map_definition_id: fieldA.map_definition_id,
				field_name: fieldA.field_name,
				idx: idxB,
				source_selector: fieldA.source_selector,
				target_path: fieldA.target_path
			});

			await Grape.fetches.postJSON('/api/json-map/field', {
				map_field_id: fieldB.map_field_id,
				map_definition_id: fieldB.map_definition_id,
				field_name: fieldB.field_name,
				idx: idxA,
				source_selector: fieldB.source_selector,
				target_path: fieldB.target_path
			});

			await this.loadFields(fieldA.map_definition_id);
		}
		catch (err)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: err.message });
		}
	}

	// --- Test panel ---

	btnToggleTestPanel()
	{
		this.showTestPanel(!this.showTestPanel());
	}

	async btnRunTest()
	{
		this.testError('');
		this.testResult('');
		const def = this.selectedDefinition();
		if (!def)
			return;

		try
		{
			let source;
			try { source = JSON.parse(this.testSource()); }
			catch (e) { throw new Error('Source must be valid JSON'); }

			let tmpl = null;
			const tmplText = (this.testTemplate() || '').trim();
			if (tmplText)
			{
				try { tmpl = JSON.parse(tmplText); }
				catch (e) { throw new Error('Template must be valid JSON'); }
			}

			const result = await def.mapObject(source, tmpl);
			if (result.status !== 'OK')
				throw new Error(result.message || 'Mapping failed');

			this.testResult(JSON.stringify(result.result || result, null, '\t'));
		}
		catch (err)
		{
			this.testError(err.message);
		}
	}
}

export default {
	name: 'json-mapper-editor',
	viewModel: JsonMapperEditorVM,
	module_type: 'ko',
	template: template
};
